import { DragDropEventHandlers, DragDropProvider } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { List } from '@mui/material';
import { useMemo } from 'react';

import PlaceholderFieldContent from './PlaceholderFieldContent';
import { TemplatePlaceholderWithNodeAndFilterContext } from './types';
import { getPlaceholderKeysInPositionOrder } from './util';

type PlaceholderFieldContentListProps = {
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>;

  /** Called when user drag and drops fields so the position values needs to be updated */
  onPositionsChange: (newPositions: Record<string, number>) => void;

  /** Called whenever any specific placeholder is changed*/
  onPlaceholderChange: (
    updatedPlaceholderData: TemplatePlaceholderWithNodeAndFilterContext
  ) => void;

  /** Placeholder hash keys (leafNodeId:filterNameAndField) that are hidden
   * because the compound filter primary is empty. */
  hiddenPlaceholderKeys: Set<string>;

  /** Optional map of placeholder key to ARIA title (for screen readers; e.g. with " A" suffix for duplicates). */
  ariaTitlesByKey?: Record<string, string>;

  /** Called when user clicks the icon next to a field indicated to be hidden */
  onScrollToLeafNodeClick: (leafNodeId: string) => void;

  /** Called when the "Add instructions" field gains focus; highlights corresponding filter on left and scrolls to it. */
  onPlaceholderClick?: (placeholderKey: string) => void;
  /** Called when the "Add instructions" field loses focus; clears the highlight. */
  onPlaceholderBlur?: () => void;
  /** Placeholder key (leafNodeId:filterNameAndField) of the entry that is selected (focused); used to show border on right. */
  selectedPlaceholderKey?: string | null;
};

export default function PlaceholderFieldContentList({
  placeholdersData,
  onPositionsChange,
  onPlaceholderChange,
  hiddenPlaceholderKeys,
  ariaTitlesByKey,
  onScrollToLeafNodeClick,
  onPlaceholderClick,
  onPlaceholderBlur,
  selectedPlaceholderKey,
}: PlaceholderFieldContentListProps) {
  const orderedKeys = useMemo(
    () => getPlaceholderKeysInPositionOrder(placeholdersData),
    [placeholdersData]
  );

  // I tried using DragEndEvent but it doesn't have the cancelled property?
  // const handleDragEnd = (event: DragEndEvent) => {
  const handleDragEnd = (event: Parameters<DragDropEventHandlers['onDragEnd']>[0]) => {
    if (event.canceled) return;

    const { source } = event.operation;

    if (isSortable(source)) {
      const { initialIndex, index: newIndex } = source;

      if (newIndex !== initialIndex) {
        const newOrderedKeys = orderedKeys
          .toSpliced(initialIndex, 1)
          .toSpliced(newIndex, 0, orderedKeys[initialIndex]);

        const newPositions: Record<string, number> = {};
        newOrderedKeys.forEach((key, index) => {
          newPositions[key] = index;
        });
        onPositionsChange(newPositions);
      }
    }
  };

  return (
    <>
      <DragDropProvider onDragEnd={handleDragEnd}>
        <List sx={{ marginLeft: 0, marginRight: 0 }}>
          {orderedKeys.map((key, idx) => {
            const placeholderData = placeholdersData[key];
            const isHidden = hiddenPlaceholderKeys.has(key);
            return (
              <PlaceholderFieldContent
                key={key}
                placeholderData={placeholderData}
                onChange={onPlaceholderChange}
                isHidden={isHidden}
                ariaTitle={ariaTitlesByKey?.[key]}
                onClickScrollToFilter={
                  isHidden ? () => onScrollToLeafNodeClick(placeholderData.leafNodeId) : undefined
                }
                onPlaceholderClick={onPlaceholderClick ? () => onPlaceholderClick(key) : undefined}
                onPlaceholderBlur={onPlaceholderBlur}
                isSelected={selectedPlaceholderKey === key}
                sortId={key}
                sortIndex={idx}
              />
            );
          })}
        </List>
      </DragDropProvider>
    </>
  );
}
